import { useState } from 'react';
import { AlertTriangle, Check, Loader2, RefreshCw, XCircle } from 'lucide-react';
import { Card, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { toast } from '@/components/ui/Toaster';
import { useRebuildWorkspace, useWorkspaceProgress } from '@/api/tenants';
import { cn } from '@/lib/cn';

/** Ordered phases of a provision run, as emitted by the backend. */
const PHASES: { key: string; label: string }[] = [
  { key: 'queued', label: 'Queued' },
  { key: 'creating_droplet', label: 'Creating droplet' },
  { key: 'waiting_for_ip', label: 'Waiting for IP' },
  { key: 'configuring_dns', label: 'DNS record' },
  { key: 'installing', label: 'Installing NousViz' },
  { key: 'smoke_test', label: 'Health check' },
  { key: 'live', label: 'Live' },
];

function elapsedLabel(sec: number | null | undefined): string {
  if (sec == null) return '';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

interface Props {
  tenantId: string;
  workspaceId: string;
  /** Workspace status from the parent query — controls polling. */
  status: string;
}

/**
 * Live phase stepper for a provisioning (or failed) workspace.
 * Renders nothing once the workspace is live with no failure to report.
 */
export function ProvisioningProgress({ tenantId, workspaceId, status }: Props) {
  const active = status === 'provisioning' || status === 'error';
  const { data } = useWorkspaceProgress(tenantId, workspaceId, active);
  const rebuild = useRebuildWorkspace();
  const [rebuilding, setRebuilding] = useState(false);

  if (!active || !data) return null;

  const failed = data.provision_status === 'failed';
  const currentIdx = PHASES.findIndex((p) => p.key === data.provision_status);
  const reached = new Set(data.provision_history.map((h) => h.status));

  async function onRebuild() {
    const reason = window.prompt(
      'Rebuild this instance? The server is wiped and set up again from scratch ' +
      'with the same web address. Enter a reason for the audit log:',
    );
    if (!reason) return;
    setRebuilding(true);
    try {
      const r = await rebuild.mutateAsync({ tenantId, workspaceId, reason });
      toast.info('Rebuild started', r.message);
    } catch (e: any) {
      toast.danger('Rebuild failed to start', e?.message ?? 'Unknown error');
    } finally {
      setRebuilding(false);
    }
  }

  return (
    <Card className={cn(failed && 'border-[color:var(--danger)]/40')}>
      <CardHeader
        title={failed ? 'Setup failed' : 'Setting up this instance'}
        description={
          failed
            ? 'The run below shows where it stopped.'
            : `Usually 6–12 minutes. Elapsed: ${elapsedLabel(data.elapsed_sec)}`
        }
        actions={
          failed ? (
            <Button variant="danger" size="sm" onClick={onRebuild} disabled={rebuilding}>
              {rebuilding
                ? <><Loader2 size={13} className="animate-spin mr-1.5" /> Starting…</>
                : <><RefreshCw size={13} className="mr-1.5" /> Rebuild</>}
            </Button>
          ) : undefined
        }
      />

      {data.stalled && !failed && (
        <div className="mx-4 mb-3 flex items-start gap-2 rounded-md border border-[color:var(--warn)]/40 bg-[color:var(--warn)]/10 px-3 py-2 text-xs">
          <AlertTriangle size={14} className="mt-0.5 shrink-0 text-[color:var(--warn)]" />
          <div>
            <div className="font-semibold">This is taking longer than expected ({elapsedLabel(data.elapsed_sec)}).</div>
            <div className="text-fg-subtle mt-0.5">
              Check the install log on the droplet
              {data.last_droplet_ip ? <>: <code className="font-mono">ssh root@{data.last_droplet_ip} tail -f /var/log/nousviz-bootstrap.log</code></> : '.'}{' '}
              Or use Rebuild to start over.
            </div>
          </div>
        </div>
      )}

      <div className="px-4 pb-4">
        <ol className="space-y-1.5">
          {PHASES.map((p, i) => {
            const done = reached.has(p.key) && (i < currentIdx || data.provision_status === 'live');
            const current = p.key === data.provision_status;
            const historyEntry = [...data.provision_history].reverse().find((h) => h.status === p.key);
            return (
              <li key={p.key} className="flex items-center gap-2.5 text-sm">
                <span className={cn(
                  'inline-flex h-5 w-5 items-center justify-center rounded-full border text-[10px]',
                  done && 'border-[color:var(--success)] bg-[color:var(--success)]/15 text-[color:var(--success)]',
                  current && !failed && 'border-accent text-accent',
                  current && failed && 'border-[color:var(--danger)] text-[color:var(--danger)]',
                  !done && !current && 'border-border text-fg-subtle',
                )}>
                  {done ? <Check size={11} />
                    : current && failed ? <XCircle size={11} />
                    : current ? <Loader2 size={11} className="animate-spin" />
                    : i + 1}
                </span>
                <span className={cn(
                  done && 'text-fg',
                  current && 'font-semibold',
                  !done && !current && 'text-fg-subtle',
                )}>
                  {p.label}
                </span>
                {current && data.provision_progress && (
                  <span className="truncate text-xs text-fg-subtle">— {data.provision_progress}</span>
                )}
                {historyEntry && !current && (
                  <span className="ml-auto text-2xs tabular-nums text-fg-subtle">
                    {new Date(historyEntry.at).toLocaleTimeString()}
                  </span>
                )}
              </li>
            );
          })}
        </ol>

        {failed && data.last_error && (
          <div className="mt-3 rounded-md border border-[color:var(--danger)]/40 bg-[color:var(--danger)]/10 px-3 py-2">
            <div className="text-2xs uppercase tracking-wider font-semibold text-[color:var(--danger)] mb-1">
              Failure reason
            </div>
            <div className="text-xs font-mono break-words">{data.last_error}</div>
          </div>
        )}
      </div>
    </Card>
  );
}
